import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faShieldAlt,
  faUserCheck,
  faLock,
  faEye,
  faSearch,
  faCheckCircle,
  faStar,
  faHeart,
  faUsers,
  faNewspaper,
  faBullhorn,
  faExclamationTriangle,
  faInfoCircle,
  faGlobe,
  faMobile,
  faDesktop,
  faRocket
} from '@fortawesome/free-solid-svg-icons';

const GoogleAuth = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 30 },
    transition: { duration: 0.6 }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const staggerItem = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 20 },
    transition: { duration: 0.5 }
  };

  const features = [
    {
      icon: faUserCheck,
      title: "For Reporters",
      description: "Register, verify your profile, and publish your stories with our comprehensive reporting tools.",
      bgColor: "from-blue-50 to-blue-100",
      color: "from-blue-500 to-blue-600",
      iconBg: "bg-blue-600",
      benefits: ["Profile verification", "Story publishing", "Community access", "Analytics dashboard"]
    },
    {
      icon: faShieldAlt,
      title: "For Advertisers", 
      description: "Submit advertisements, set view targets, and collaborate with reporters to increase visibility.",
      bgColor: "from-purple-50 to-purple-100",
      color: "from-purple-500 to-purple-600",
      iconBg: "bg-purple-600",
      benefits: ["Ad submission", "Target setting", "Reporter collaboration", "Performance tracking"]
    },
    {
      icon: faEye,
      title: "For Audiences",
      description: "Access reliable, community-driven news and updates from verified sources.",
      bgColor: "from-green-50 to-green-100",
      color: "from-green-500 to-green-600",
      iconBg: "bg-green-600",
      benefits: ["Verified content", "Real-time updates", "Community insights", "Trusted sources"]
    }
  ];

  const permissions = [
    {
      title: "Basic Profile Information",
      description: "(Name, Email, Profile Picture) – Used to create and display your account.",
      icon: faUsers,
      color: "from-blue-500 to-blue-600",
      scopes: ["profile", "email"],
      required: true
    },
    {
      title: "Email Address",
      description: "Used for account verification, password recovery, and sending important updates.",
      icon: faMobile,
      color: "from-green-500 to-green-600",
      scopes: ["email"],
      required: true
    },
    {
      title: "YouTube Integration",
      description: "Access to YouTube channel data for content creators and video analytics.",
      icon: faRocket,
      color: "from-purple-500 to-purple-600",
      scopes: ["https://www.googleapis.com/auth/youtube.readonly"],
      required: false
    },
    {
      title: "Google Drive Access",
      description: "Optional access to Google Drive for file storage and document management.",
      icon: faDesktop,
      color: "from-orange-500 to-orange-600",
      scopes: ["https://www.googleapis.com/auth/drive.file"],
      required: false
    }
  ];

  const dataUsage = [
    "Authentication and secure login",
    "Displaying your profile (reporter/advertiser identity)",
    "Enabling communication between advertisers and reporters",
    "Providing notifications about campaigns, ads, or platform updates",
    "Improving platform features and user experience"
  ];

  const verificationSteps = [
    {
      step: 1,
      title: "Google Cloud Console Setup",
      description: "Create a project in Google Cloud Console and enable Google+ API",
      details: [
        "Navigate to Google Cloud Console (console.cloud.google.com)",
        "Create a new project or select existing one",
        "Enable Google+ API and Google Identity API",
        "Configure OAuth consent screen",
        "Set up authorized redirect URIs"
      ],
      icon: faGlobe,
      color: "from-blue-500 to-blue-600"
    },
    {
      step: 2,
      title: "OAuth 2.0 Configuration",
      description: "Configure OAuth 2.0 credentials and consent screen",
      details: [
        "Create OAuth 2.0 Client ID credentials",
        "Set application type to 'Web application'",
        "Add authorized JavaScript origins",
        "Configure authorized redirect URIs",
        "Set up OAuth consent screen with app information",
        "Upload app logo (120x120px, max 1MB, JPG/PNG/BMP format)"
      ],
      icon: faLock,
      color: "from-green-500 to-green-600"
    },
    {
      step: 3,
      title: "Domain Verification",
      description: "Verify your domain ownership for production deployment",
      details: [
        "Add domain verification in Google Search Console",
        "Upload verification file to your domain root",
        "Complete domain verification process",
        "Update OAuth consent screen with verified domain",
        "Submit for Google verification if required"
      ],
      icon: faShieldAlt,
      color: "from-purple-500 to-purple-600"
    },
    {
      step: 4,
      title: "Implementation & Testing",
      description: "Implement Google Auth in your application and test thoroughly",
      details: [
        "Integrate Google Sign-In JavaScript library",
        "Implement OAuth 2.0 flow in backend",
        "Test authentication flow in development",
        "Test with different user accounts",
        "Deploy to production with verified domain"
      ],
      icon: faRocket,
      color: "from-orange-500 to-orange-600"
    }
  ];

  const technicalRequirements = [
    {
      category: "Frontend Requirements",
      items: [
        "Google Sign-In JavaScript library (gapi)",
        "HTTPS enabled domain for production",
        "Proper CORS configuration",
        "Secure token handling and storage"
      ]
    },
    {
      category: "Backend Requirements", 
      items: [
        "OAuth 2.0 server implementation",
        "JWT token validation",
        "Secure API endpoints",
        "Database integration for user data"
      ]
    },
    {
      category: "Security Requirements",
      items: [
        "HTTPS enforcement",
        "CSRF protection",
        "Secure session management",
        "Regular security audits"
      ]
    }
  ];

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-slate-50 via-white to-blue-50 pt-20 pb-8 px-2 sm:px-4">
      {/* Header Section */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: isVisible ? 1 : 0, scale: isVisible ? 1 : 0.95 }}
        transition={{ duration: 0.8 }}
        className="w-full max-w-7xl mx-auto mb-16"
      >
        <div className="bg-white/80 backdrop-blur-lg rounded-3xl shadow-2xl border border-white/20 p-8 sm:p-12">
          <div className="text-center">
            <div className="inline-block bg-gradient-to-r from-blue-500 to-blue-600 text-white px-6 py-2 rounded-full text-sm font-semibold mb-4 shadow-lg">
              GOOGLE AUTHENTICATION & PRIVACY
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold mb-6">
              Your <span className="bg-gradient-to-r from-blue-500 to-blue-600 bg-clip-text text-transparent">Security</span> Matters
            </h1>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-blue-600 mx-auto rounded-full mb-6"></div>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Your security and privacy are our top priorities. Learn how we protect your data and why we use Google authentication.
            </p>
          </div>
        </div>
      </motion.div>

      {/* About IINSAF Section */}
      <div className="w-full max-w-7xl mx-auto mb-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 30 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="bg-white/80 backdrop-blur-lg rounded-3xl shadow-2xl border border-white/20 p-8 sm:p-12"
        >
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-4">
              About <span className="bg-gradient-to-r from-blue-500 to-blue-600 bg-clip-text text-transparent">IINSAF</span>
            </h2>
            <p className="text-lg text-gray-700 max-w-4xl mx-auto leading-relaxed">
              IINSAF is a digital platform designed to provide a transparent space for reporters and advertisers. 
              Our mission is to empower individuals to share verified news, raise awareness on important issues, 
              and help advertisers connect with credible voices.
            </p>
          </div>

          {/* Features Grid */}
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
          >
            {features.map((feature, index) => (
              <motion.div
                key={index}
                variants={staggerItem}
                className={`bg-gradient-to-br ${feature.bgColor} rounded-2xl p-6 text-center hover:shadow-xl transition-all duration-300 hover:-translate-y-2 group cursor-pointer border border-white/20`}
              >
                <div className={`${feature.iconBg} w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                  <FontAwesomeIcon icon={feature.icon} className="text-white text-2xl" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-700 mb-4 leading-relaxed">
                  {feature.description}
                </p>
                <div className="space-y-2">
                  {feature.benefits.map((benefit, idx) => (
                    <div key={idx} className="flex items-center justify-center gap-2">
                      <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 text-sm" />
                      <span className="text-sm text-gray-600">{benefit}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </div>

      {/* Google Authentication Section */}
      <div className="w-full max-w-7xl mx-auto mb-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 30 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="bg-white/80 backdrop-blur-lg rounded-3xl shadow-2xl border border-white/20 p-8 sm:p-12"
        >
          <div className="flex items-center justify-center mb-8">
            <div className="w-20 h-20 bg-gradient-to-r from-red-500 to-red-600 rounded-2xl flex items-center justify-center mr-4 shadow-lg">
              <FontAwesomeIcon icon={faSearch} className="text-white text-3xl" />
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-800">Why We Use Google Authentication</h2>
          </div>
          
          <p className="text-lg text-gray-700 text-center mb-10 max-w-4xl mx-auto leading-relaxed">
            To keep our platform safe and secure, IINSAF uses Google authentication (OAuth 2.0). 
            When you sign in with Google, we request certain permissions to verify your identity 
            and personalize your experience.
          </p>

          {/* Permissions Grid */}
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10"
          >
            {permissions.map((permission, index) => (
              <motion.div
                key={index}
                variants={staggerItem}
                className="bg-white/50 backdrop-blur-sm rounded-2xl p-6 border border-white/20 hover:bg-white/80 transition-all duration-300 hover:shadow-lg"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className={`w-12 h-12 bg-gradient-to-r ${permission.color} rounded-xl flex items-center justify-center shadow-lg`}>
                    <FontAwesomeIcon icon={permission.icon} className="text-white text-xl" />
                  </div>
                  <div className={`px-3 py-1 rounded-full text-xs font-semibold ${permission.required ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>
                    {permission.required ? 'Required' : 'Optional'}
                  </div>
                </div>
                <h4 className="font-bold text-gray-800 mb-2">{permission.title}</h4>
                <p className="text-gray-600 text-sm leading-relaxed mb-3">{permission.description}</p>
                <div className="space-y-1">
                  <p className="text-xs font-semibold text-gray-700">OAuth Scopes:</p>
                  <div className="flex flex-wrap gap-1">
                    {permission.scopes.map((scope, idx) => (
                      <span key={idx} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-md font-mono">
                        {scope}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Privacy Assurance */}
          <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-2xl p-6">
            <div className="flex items-start">
              <div className="w-12 h-12 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                <FontAwesomeIcon icon={faExclamationTriangle} className="text-white text-xl" />
              </div>
              <div>
                <p className="text-gray-800 font-medium leading-relaxed">
                  We only request the minimum data required to operate the platform. Your information 
                  will <strong className="text-red-600">never be sold or shared</strong> with third parties without your consent.
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Google Auth Verification Steps */}
      <div className="w-full max-w-7xl mx-auto mb-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 30 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="bg-white/80 backdrop-blur-lg rounded-3xl shadow-2xl border border-white/20 p-8 sm:p-12"
        >
          <div className="text-center mb-12">
            <div className="inline-block bg-gradient-to-r from-purple-500 to-purple-600 text-white px-6 py-2 rounded-full text-sm font-semibold mb-4 shadow-lg">
              GOOGLE AUTH VERIFICATION GUIDE
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-4">
              Step-by-Step <span className="bg-gradient-to-r from-purple-500 to-purple-600 bg-clip-text text-transparent">Verification Process</span>
            </h2>
            <p className="text-lg text-gray-700 max-w-4xl mx-auto leading-relaxed">
              Follow these detailed steps to properly set up and verify Google Authentication for your application.
            </p>
          </div>

          {/* Verification Steps */}
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="space-y-8"
          >
            {verificationSteps.map((step, index) => (
              <motion.div
                key={index}
                variants={staggerItem}
                className="bg-gradient-to-r from-white/80 to-white/60 backdrop-blur-sm rounded-2xl p-6 sm:p-8 border border-white/20 hover:shadow-xl transition-all duration-300"
              >
                <div className="flex items-start gap-6">
                  <div className={`w-16 h-16 bg-gradient-to-r ${step.color} rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0`}>
                    <FontAwesomeIcon icon={step.icon} className="text-white text-2xl" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-4 mb-3">
                      <div className={`w-8 h-8 bg-gradient-to-r ${step.color} rounded-full flex items-center justify-center text-white font-bold text-sm`}>
                        {step.step}
                      </div>
                      <h3 className="text-xl sm:text-2xl font-bold text-gray-800">{step.title}</h3>
                    </div>
                    <p className="text-gray-700 mb-4 leading-relaxed">{step.description}</p>
                    <div className="space-y-2">
                      {step.details.map((detail, idx) => (
                        <motion.div
                          key={idx}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.3, delay: idx * 0.1 }}
                          className="flex items-center gap-3"
                        >
                          <div className={`w-2 h-2 bg-gradient-to-r ${step.color} rounded-full flex-shrink-0`}></div>
                          <span className="text-gray-600 text-sm">{detail}</span>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </div>

      {/* Logo Requirements Section */}
      <div className="w-full max-w-7xl mx-auto mb-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 30 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="bg-white/80 backdrop-blur-lg rounded-3xl shadow-2xl border border-white/20 p-8 sm:p-12"
        >
          <div className="text-center mb-12">
            <div className="inline-block bg-gradient-to-r from-pink-500 to-pink-600 text-white px-6 py-2 rounded-full text-sm font-semibold mb-4 shadow-lg">
              APP LOGO REQUIREMENTS
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-4">
              OAuth Consent Screen <span className="bg-gradient-to-r from-pink-500 to-pink-600 bg-clip-text text-transparent">Logo</span>
            </h2>
            <p className="text-lg text-gray-700 max-w-4xl mx-auto leading-relaxed">
              Specifying a logo for your app helps people recognize your app when the sign-in and consent screens are presented to them.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Logo Requirements */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: isVisible ? 1 : 0, x: isVisible ? 0 : -20 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="bg-gradient-to-br from-pink-50 to-rose-100 rounded-2xl p-6 border border-pink-200"
            >
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-r from-pink-500 to-pink-600 rounded-xl flex items-center justify-center mr-4">
                  <FontAwesomeIcon icon={faInfoCircle} className="text-white text-xl" />
                </div>
                <h3 className="text-xl font-bold text-gray-800">Logo Requirements</h3>
              </div>
              
              {/* Logo Preview */}
              <div className="mb-6 flex justify-center">
                <div className="bg-white rounded-2xl p-4 shadow-lg border-2 border-pink-300">
                  <img 
                    src="/assets/logo.jpg" 
                    alt="IINSAF Logo" 
                    className="w-32 h-32 object-contain rounded-xl"
                  />
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 text-sm mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-gray-800 mb-1">Image Representation</p>
                    <p className="text-gray-600 text-sm">Uploaded image should actually represent your app</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 text-sm mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-gray-800 mb-1">File Size</p>
                    <p className="text-gray-600 text-sm">Image should not be larger than 1MB</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 text-sm mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-gray-800 mb-1">Supported Formats</p>
                    <p className="text-gray-600 text-sm">JPG, PNG, and BMP formats are supported</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 text-sm mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-gray-800 mb-1">Dimensions</p>
                    <p className="text-gray-600 text-sm">Image should be square and 120px by 120px for the best display results</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Logo Preview/Info */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: isVisible ? 1 : 0, x: isVisible ? 0 : 20 }}
              transition={{ duration: 0.6, delay: 0.7 }}
              className="bg-gradient-to-br from-blue-50 to-indigo-100 rounded-2xl p-6 border border-blue-200"
            >
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl flex items-center justify-center mr-4">
                  <FontAwesomeIcon icon={faEye} className="text-white text-xl" />
                </div>
                <h3 className="text-xl font-bold text-gray-800">Best Practices</h3>
              </div>
              
              {/* Logo Preview */}
              <div className="mb-6 flex justify-center">
                <div className="bg-white rounded-2xl p-4 shadow-lg border-2 border-blue-300">
                  <img 
                    src="/assets/logo.jpg" 
                    alt="IINSAF Logo" 
                    className="w-32 h-32 object-contain rounded-xl"
                  />
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="bg-white/80 rounded-xl p-4">
                  <p className="text-gray-700 text-sm leading-relaxed mb-3">
                    <strong className="text-gray-800">Why it matters:</strong> Your logo appears on Google's consent screen, 
                    helping users recognize and trust your application during the authentication process.
                  </p>
                </div>
                <div className="bg-white/80 rounded-xl p-4">
                  <p className="text-gray-700 text-sm leading-relaxed mb-2">
                    <strong className="text-gray-800">Tips for best results:</strong>
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-gray-600 text-sm">
                    <li>Use a high-quality, recognizable logo</li>
                    <li>Ensure the logo is centered and properly cropped</li>
                    <li>Use transparent background if possible (PNG)</li>
                    <li>Test how it looks on both light and dark themes</li>
                  </ul>
                </div>
                <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-xl p-4">
                  <div className="flex items-start gap-2">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="text-yellow-600 text-sm mt-1 flex-shrink-0" />
                    <p className="text-yellow-800 text-sm">
                      <strong>Note:</strong> Make sure your logo complies with Google's brand guidelines and doesn't violate any trademark or copyright restrictions.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>

      {/* Technical Requirements Section */}
      <div className="w-full max-w-7xl mx-auto mb-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 30 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="bg-white/80 backdrop-blur-lg rounded-3xl shadow-2xl border border-white/20 p-8 sm:p-12"
        >
          <div className="text-center mb-12">
            <div className="inline-block bg-gradient-to-r from-indigo-500 to-indigo-600 text-white px-6 py-2 rounded-full text-sm font-semibold mb-4 shadow-lg">
              TECHNICAL REQUIREMENTS
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-4">
              Implementation <span className="bg-gradient-to-r from-indigo-500 to-indigo-600 bg-clip-text text-transparent">Requirements</span>
            </h2>
            <p className="text-lg text-gray-700 max-w-4xl mx-auto leading-relaxed">
              Ensure your application meets all technical requirements for successful Google Auth integration.
            </p>
          </div>

          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
          >
            {technicalRequirements.map((requirement, index) => (
              <motion.div
                key={index}
                variants={staggerItem}
                className="bg-gradient-to-br from-white/60 to-white/40 backdrop-blur-sm rounded-2xl p-6 border border-white/20 hover:shadow-lg transition-all duration-300"
              >
                <h3 className="text-xl font-bold text-gray-800 mb-4 text-center">{requirement.category}</h3>
                <div className="space-y-3">
                  {requirement.items.map((item, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: idx * 0.1 }}
                      className="flex items-start gap-3"
                    >
                      <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 text-sm mt-1 flex-shrink-0" />
                      <span className="text-gray-700 text-sm">{item}</span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </div>

      {/* Data Usage Section */}
      <div className="w-full max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 30 }}
          transition={{ duration: 0.8, delay: 0.7 }}
          className="grid grid-cols-1 lg:grid-cols-2 gap-8"
        >
          {/* Data Usage */}
          <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl border border-white/20 p-8">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl flex items-center justify-center mr-4">
                <FontAwesomeIcon icon={faInfoCircle} className="text-white text-xl" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-800">How We Use Your Data</h2>
            </div>
            <p className="text-lg text-gray-700 mb-6">
              Your data is used only for the following purposes:
            </p>
            <div className="space-y-4">
              {dataUsage.map((usage, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  className="flex items-center bg-white/50 backdrop-blur-sm p-4 rounded-xl shadow-sm hover:shadow-md transition-all duration-300"
                >
                  <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                    <span className="text-white font-bold text-sm">{index + 1}</span>
                  </div>
                  <p className="text-gray-700">{usage}</p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Security & Transparency */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-100 rounded-2xl shadow-xl border border-white/20 p-8">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl flex items-center justify-center mr-4">
                <FontAwesomeIcon icon={faLock} className="text-white text-xl" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-800">Transparency & Security</h2>
            </div>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              We believe in complete transparency. You will always know what data is being 
              accessed and why. We use industry-standard security practices to protect 
              your personal information and ensure your account remains safe.
            </p>
            
            <div className="bg-green-50 border border-green-200 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 text-lg" />
                <p className="text-green-800">
                  All collected information is handled in accordance with our{' '}
                  <a href="/privacypolicy" className="text-blue-600 font-semibold hover:text-blue-800 hover:underline">
                    Privacy Policy
                  </a>.
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Implementation Examples & Troubleshooting */}
      <div className="w-full max-w-7xl mx-auto mb-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 30 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="grid grid-cols-1 lg:grid-cols-2 gap-8"
        >
          {/* Implementation Examples */}
          <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl border border-white/20 p-8">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-green-600 rounded-xl flex items-center justify-center mr-4">
                <FontAwesomeIcon icon={faRocket} className="text-white text-xl" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-800">Quick Implementation</h2>
            </div>
            <div className="space-y-4">
              <div className="bg-gray-900 rounded-xl p-4 text-green-400 text-sm font-mono overflow-x-auto">
                <div className="text-gray-400 mb-2">// Frontend Implementation</div>
                <div>const clientId = &quot;YOUR_CLIENT_ID&quot;;</div>
                <div>const scope = &quot;profile email&quot;;</div>
                <div>const redirectUri = &quot;YOUR_REDIRECT_URI&quot;;</div>
                <div className="mt-2 text-gray-400">// Backend Verification</div>
                <div>const &#123; OAuth2Client &#125; = require(&apos;google-auth-library&apos;);</div>
                <div>const client = new OAuth2Client(CLIENT_ID);</div>
                <div>const ticket = await client.verifyIdToken(&#123;</div>
                <div className="ml-4">idToken: token,</div>
                <div className="ml-4">audience: CLIENT_ID</div>
                <div>&#125;);</div>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <FontAwesomeIcon icon={faInfoCircle} className="text-blue-500" />
                  <span className="font-semibold text-blue-800">Pro Tip</span>
                </div>
                <p className="text-blue-700 text-sm">
                  Always validate tokens on the backend and never trust client-side authentication alone.
                </p>
              </div>
            </div>
          </div>

          {/* Troubleshooting */}
          <div className="bg-gradient-to-br from-red-50 to-orange-100 rounded-2xl shadow-xl border border-white/20 p-8">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-gradient-to-r from-red-500 to-orange-500 rounded-xl flex items-center justify-center mr-4">
                <FontAwesomeIcon icon={faExclamationTriangle} className="text-white text-xl" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-800">Common Issues</h2>
            </div>
            <div className="space-y-4">
              <div className="bg-white/80 rounded-xl p-4">
                <h4 className="font-semibold text-gray-800 mb-2">❌ "Invalid Client ID"</h4>
                <p className="text-gray-600 text-sm">Verify your Client ID in Google Cloud Console and ensure it matches your domain.</p>
              </div>
              <div className="bg-white/80 rounded-xl p-4">
                <h4 className="font-semibold text-gray-800 mb-2">❌ "Redirect URI Mismatch"</h4>
                <p className="text-gray-600 text-sm">Add your exact domain URLs to authorized redirect URIs in OAuth settings.</p>
              </div>
              <div className="bg-white/80 rounded-xl p-4">
                <h4 className="font-semibold text-gray-800 mb-2">❌ "Access Blocked"</h4>
                <p className="text-gray-600 text-sm">Complete domain verification and submit for Google review if required.</p>
              </div>
              <div className="bg-white/80 rounded-xl p-4">
                <h4 className="font-semibold text-gray-800 mb-2">❌ "Token Verification Failed"</h4>
                <p className="text-gray-600 text-sm">Ensure your backend is properly validating JWT tokens with the correct audience.</p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Contact Support Section */}
      <div className="w-full max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 30 }}
          transition={{ duration: 0.8, delay: 0.9 }}
          className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-3xl shadow-xl p-8 sm:p-12 text-white text-center"
        >
          <div className="flex items-center justify-center mb-6">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mr-4">
              <FontAwesomeIcon icon={faHeart} className="text-white text-2xl" />
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold">Need Help with Implementation?</h2>
          </div>
          <p className="text-lg mb-8 opacity-90 max-w-3xl mx-auto leading-relaxed">
            Our technical team is here to help you implement Google Authentication successfully. 
            Contact us for personalized assistance with your integration.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="/contact" 
              className="bg-white text-blue-600 px-8 py-3 rounded-xl font-semibold hover:bg-gray-100 transition-colors duration-300"
            >
              Contact Support
            </a>
            <a 
              href="https://developers.google.com/identity" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-white/20 text-white px-8 py-3 rounded-xl font-semibold hover:bg-white/30 transition-colors duration-300 border border-white/30"
            >
              Google Documentation
            </a>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default GoogleAuth;